import React, { useState } from 'react';

import {
  DeleteOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { formatDateTime } from '@utils/dateTime';
import { Upload, message, Popconfirm, Spin, Tag, Empty } from 'antd';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

const FILE_ICONS = {
  'application/pdf': <FilePdfOutlined style={{ color: '#ff4d4f' }} />,
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': <FileExcelOutlined style={{ color: '#52c41a' }} />,
  'application/vnd.ms-excel': <FileExcelOutlined style={{ color: '#52c41a' }} />,
};

function getFileIcon(contentType) {
  if (FILE_ICONS[contentType]) return FILE_ICONS[contentType];
  if (contentType?.startsWith('image/')) return <FileImageOutlined style={{ color: '#1677ff' }} />;
  return <FileOutlined style={{ color: '#888' }} />;
}

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * 訂單附件區塊。
 * 顯示兩來源附件：手動上傳（source='manual'）與匯入批次共享（source='import'，永遠唯讀）。
 * readOnly=true 時純顯示 / 下載（詳情頁用）：不顯示上傳區與刪除鈕；
 * readOnly=false 時可上傳（走 createAttachment）、可刪除手動附件（走 deleteAttachment）（編輯表單用）。
 */
export default function OrderAttachments({ attachments, orderId, service, onUpdate, readOnly = false }) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await service.createAttachment(orderId, file);
      if (res.success) {
        message.success(t('common.submit.success'));
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleDelete = async (attachmentId) => {
    try {
      const res = await service.deleteAttachment(orderId, attachmentId);
      if (res.success) {
        message.success(t('common.submit.success'));
        onUpdate();
      } else {
        handleApiError(res, { t });
      }
    } catch (error) {
      handleNetworkError(error, { t });
    }
  };

  return (
    <Spin spinning={uploading}>
      <div style={{ fontSize: 12 }}>
        {(!attachments || attachments.length === 0) && (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('shared.orders.attachments.empty')} />
        )}
        {attachments?.map((att) => (
          <div
            key={`${att.source}-${att.id}`}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '6px 0',
              borderBottom: '1px solid #f5f5f5',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', flex: 1 }}>
              {getFileIcon(att.content_type)}
              <div style={{ overflow: 'hidden' }}>
                <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <a href={att.file_url} target="_blank" rel="noopener noreferrer">
                    {att.filename}
                  </a>
                  {att.source === 'import' && (
                    <Tag color="blue" style={{ marginLeft: 8 }}>
                      {t('shared.orders.attachments.source_import')}
                    </Tag>
                  )}
                </div>
                <div style={{ fontSize: 10, color: '#aaa' }}>
                  {formatDateTime(att.created_at)}
                  {att.byte_size ? ` · ${formatFileSize(att.byte_size)}` : ''}
                </div>
              </div>
            </div>
            {!readOnly && att.source === 'manual' && (
              <Popconfirm
                title={t('shared.orders.attachments.delete_confirm')}
                onConfirm={() => handleDelete(att.id)}
                okText={t('common.button.confirm')}
                cancelText={t('common.button.cancel')}
              >
                <DeleteOutlined style={{ color: '#ff4d4f', cursor: 'pointer', flexShrink: 0, marginLeft: 8 }} />
              </Popconfirm>
            )}
          </div>
        ))}
      </div>

      {!readOnly && (
        <Dragger
          showUploadList={false}
          beforeUpload={handleUpload}
          multiple={false}
          style={{ marginTop: 8 }}
        >
          <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
            <InboxOutlined />
          </p>
          <p style={{ fontSize: 11, color: '#aaa', margin: 0 }}>
            {t('shared.orders.attachments.upload_hint')}
          </p>
          <p style={{ fontSize: 11, color: '#1677ff', margin: 0 }}>
            {t('shared.orders.attachments.upload_or_click')}
          </p>
        </Dragger>
      )}
    </Spin>
  );
}
